import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';

import { AcaoEmLink } from './AcaoEmLink';
import type { ResultadoDoDestino } from './aplicarAosDestinos';
import { contar, flexionar } from './textosDoNivel';
import type { DestinoRef } from './tipos';

interface ResultadoDaAplicacaoProps<V> {
  resultados: ResultadoDoDestino<V>[];
  rotulos: { singular: string; plural: string; feminino?: boolean };
  linkDaFicha?: (destino: DestinoRef) => string | null;
  repetindo: boolean;
  aoRepetirFalhos: () => void;
}

/** "2 riscos aplicados a Soldador · 1 já existia · 0 falharam" — as três contagens, sempre. */
function linhaDoDestino<V>(resultado: ResultadoDoDestino<V>, rotulos: ResultadoDaAplicacaoProps<V>['rotulos']): string {
  const f = (palavra: string) => flexionar(rotulos.feminino, palavra);
  return [
    `${contar(resultado.aplicados.length, `${rotulos.singular} ${f('aplicado')}`, `${rotulos.plural} ${f('aplicados')}`)} a ${resultado.destino.nome}`,
    contar(resultado.jaExistiam.length, 'já existia', 'já existiam'),
    contar(resultado.falharam.length, 'falhou', 'falharam'),
  ].join(' · ');
}

/**
 * A prova de que deu certo, que não some sozinha (#606, D4): por destino, o que foi aplicado,
 * o que já existia e o que falhou, cada falha pelo nome — e o caminho para repetir só o que falhou.
 */
export function ResultadoDaAplicacao<V>({
  resultados,
  rotulos,
  linkDaFicha,
  repetindo,
  aoRepetirFalhos,
}: ResultadoDaAplicacaoProps<V>) {
  const falhas = resultados.reduce((soma, r) => soma + r.falharam.length, 0);
  const variosDestinos = resultados.length > 1;

  return (
    <Alert
      severity={falhas > 0 ? 'warning' : 'success'}
      sx={{ mb: 2 }}
      action={
        falhas > 0 ? (
          <AcaoEmLink cor="inherit" onClick={aoRepetirFalhos} disabled={repetindo}>
            {repetindo ? 'Repetindo…' : `Repetir ${flexionar(rotulos.feminino, 'os')} que falharam`}
          </AcaoEmLink>
        ) : undefined
      }
    >
      <Stack spacing={1}>
        {resultados.map((resultado) => {
          const ficha = variosDestinos ? (linkDaFicha?.(resultado.destino) ?? null) : null;
          return (
            <Box key={`${resultado.destino.nivel}:${resultado.destino.id}`}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ sm: 2 }} alignItems={{ sm: 'baseline' }}>
                <Typography variant="body2">{linhaDoDestino(resultado, rotulos)}</Typography>
                {ficha && (
                  <Link component={RouterLink} to={ficha} variant="body2">
                    Ver ficha de {resultado.destino.nome}
                  </Link>
                )}
              </Stack>
              {resultado.falharam.map((falha) => (
                <Typography key={falha.itemId} variant="body2">
                  {falha.nome}: {falha.motivo}
                </Typography>
              ))}
            </Box>
          );
        })}
      </Stack>
    </Alert>
  );
}
